// react
import React, { useState, useEffect } from "react";

// react-i18next
import { useTranslation } from "react-i18next";

// context
import { UserAuth } from "context/AuthContext";

// @mui
import {
  Box,
  SwipeableDrawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  IconButton,
  Typography,
  useMediaQuery,
  TableRow,
  TableCell,
  TableContainer,
  TableHead,
  TableBody,
  Table,
  CircularProgress,
  Tooltip,
  useTheme,
} from "@mui/material";
import { CloseOutlined as CloseOutlinedIcon, Add as AddIcon, VisibilityOff as VisibilityOffIcon, Visibility as VisibilityIcon, Check as CheckIcon } from "@mui/icons-material";

// ui-components
import { Puller } from "ui-components/Puller";
import { DatakeyAddedSuccessfulSnackbar } from "ui-components/ORFeedbacks";

// datakey-controller
import getDataKeysByOwner from "datakey-controller/getDataKeysByOwner";

// databox-controller
import { addAuthorizedKey } from "databox-controller/manageAuthorizedKey";

// A ---------------------------------------------------------------------- M

const AddDataKey = ({ tag, dataKeys, open, setOpen }) => {
  const { user } = UserAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [keys, setKeys] = useState([]);
  const [addedKeys, setAddedKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    const handlePopstate = () => {
      if (open) handleReset();
    };
    window.addEventListener("popstate", handlePopstate);
    return () => window.removeEventListener("popstate", handlePopstate);
  }, [open]);

  useEffect(() => {
    const fetchKeys = async () => {
      setIsLoading(true);
      try {
        const fetchedKeys = await getDataKeysByOwner(user.uid);
        setKeys(fetchedKeys);
      } catch (error) {
        console.error("Error fetching data keys:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (open) {
      fetchKeys();
    }
  }, [open, user.uid, dataKeys]);

  const isKeyAdded = (keyId) => addedKeys.includes(keyId) || dataKeys.some((key) => key.id === keyId);

  const handleAddKey = async (keyId) => {
    try {
      await addAuthorizedKey(tag.id, keyId, user.uid);
      setAddedKeys((prev) => [...prev, keyId]);
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error adding key:", error);
    }
  };

  const handleReset = () => {
    setAddedKeys([]);
    setOpen(false);
  };

  const TableRowWithToggle = ({ name, datakey, added, handleAdd }) => {
    const [showDataKey, setShowDataKey] = useState(false);
    const toggleDataKeyVisibility = () => setShowDataKey((prev) => !prev);

    return (
      <TableRow>
        <TableCell>{name}</TableCell>

        <TableCell
          align="left"
          sx={{
            minWidth: showDataKey ? "200px" : "60px",
            maxWidth: showDataKey ? "200px" : "60px",
          }}
        >
          {showDataKey ? datakey : "••••••"}
          <IconButton onClick={toggleDataKeyVisibility} size="small" color="primary">
            {showDataKey ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
          </IconButton>
        </TableCell>

        <TableCell align="right" size="small">
          {!added ? (
            <IconButton color="primary" onClick={handleAdd}>
              <AddIcon fontSize="small" />
            </IconButton>
          ) : (
            <IconButton disabled>
              <CheckIcon fontSize="small" />
            </IconButton>
          )}
        </TableCell>
      </TableRow>
    );
  };

  const renderTable = () => (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t("name")}</TableCell>
            <TableCell>{t("key")}</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>

        <TableBody>
          {keys.map((key) => {
            const added = isKeyAdded(key.id);
            return <TableRowWithToggle key={key.id} name={key.name} datakey={key.datakey} added={added} handleAdd={() => !added && handleAddKey(key.id)} />;
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const innerContent = (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {renderTable()}
      </Grid>
    </Grid>
  );

  if (isLoading) {
    return (
      <Grid item container justifyContent="center" mt="30%">
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <>
      {isMobile ? (
        <SwipeableDrawer
          anchor="bottom"
          open={open}
          onClose={handleReset}
          onOpen={() => setOpen(true)}
          sx={{
            "& .MuiDrawer-paper": {
              width: "100%",
              height: "90%",
              borderTopLeftRadius: "4%",
              borderTopRightRadius: "4%",
            },
          }}
        >
          <Puller />

          <Box sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} textAlign="center" mb={3}>
              {t("add_datakey")}
            </Typography>

            {innerContent}

            <Grid container spacing={2} mt={5}>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  sx={{
                    borderRadius: 3,
                    textTransform: "none",
                    fontWeight: 600,
                    px: 2,
                    py: 1,
                  }}
                  onClick={handleReset}
                >
                  {t("close")}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </SwipeableDrawer>
      ) : (
        <Dialog
          open={open}
          onClose={handleReset}
          maxWidth="sm"
          fullWidth
          sx={{
            "& .MuiPaper-root": {
              borderRadius: 4,
              background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}EE)`,
              backdropFilter: "blur(10px)",
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
              transition: "all 0.3s ease",
            },
          }}
        >
          <DialogTitle>
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Typography variant="h6" fontWeight={700}>
                  {t("add_datakey")}
                </Typography>
              </Grid>
              <Grid item>
                <Tooltip title={t("close")} placement="top">
                  <IconButton onClick={handleReset} color="error" edge="end">
                    <CloseOutlinedIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </DialogTitle>

          <DialogContent>{innerContent}</DialogContent>

          <DialogActions sx={{ p: 2.5 }}>
            <Button
              variant="contained"
              sx={{
                borderRadius: 3,
                textTransform: "none",
                fontWeight: 600,
                px: 2,
                py: 1,
              }}
              onClick={handleReset}
            >
              {t("close")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <DatakeyAddedSuccessfulSnackbar open={snackbarOpen} setOpen={setSnackbarOpen} />
    </>
  );
};

export default AddDataKey;
